// PoSignInviteService.js
//
// Client wrappers for PO vendor signature capability-link callables.

import { APP_BASE_URL, functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const callable = (name) => httpsCallable(functions, name);

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * @param {string} token
 * @returns {string}
 */
export function buildPoSignShareUrl(token) {
  const cleaned = cleanString(token);
  if (!cleaned) return '';
  const base = cleanString(APP_BASE_URL).replace(/\/+$/, '') || 'https://app.appdeck.ai';
  return `${base}/po-sign/${encodeURIComponent(cleaned)}`;
}

/**
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.poKey
 * @param {string} [options.expirationKey]
 */
export async function createPoSignInvite({ baseKey, poKey, expirationKey = '7d' }) {
  if (!baseKey || !poKey) {
    throw new Error('baseKey and poKey are required');
  }
  const fn = callable('createPoSignInvite');
  const { data } = await fn({
    ...buildTenantCallableFields(baseKey),
    poKey,
    expirationKey,
  });
  return data || null;
}

/**
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.poKey
 */
export async function revokePoSignInvite({ baseKey, poKey }) {
  if (!baseKey || !poKey) {
    throw new Error('baseKey and poKey are required');
  }
  const fn = callable('revokePoSignInvite');
  const { data } = await fn({
    ...buildTenantCallableFields(baseKey),
    poKey,
  });
  return data || null;
}

/**
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.poKey
 */
export async function getPoSignInvite({ baseKey, poKey }) {
  if (!baseKey || !poKey) {
    throw new Error('baseKey and poKey are required');
  }
  const fn = callable('getPoSignInvite');
  const { data } = await fn({
    ...buildTenantCallableFields(baseKey),
    poKey,
  });
  return data?.invite || null;
}

/**
 * Email the vendor sign link (uses DOCUMENT_EMAIL_RECIPIENT_OVERRIDE when set).
 *
 * @param {object} options
 * @param {string} options.baseKey
 * @param {string} options.poKey
 * @param {string} [options.expirationKey]
 * @param {string} [options.toEmail] - Optional override when vendor email is missing
 */
export async function sendPoSignInviteEmail({
  baseKey,
  poKey,
  expirationKey = '30d',
  toEmail = '',
} = {}) {
  if (!baseKey || !poKey) {
    throw new Error('baseKey and poKey are required');
  }
  const fn = callable('sendPoSignInviteEmail');
  const payload = {
    ...buildTenantCallableFields(baseKey),
    poKey,
    expirationKey,
  };
  const cleanedTo = cleanString(toEmail);
  if (cleanedTo) payload.toEmail = cleanedTo;
  const { data } = await fn(payload);
  return data || null;
}

/**
 * @param {object} options
 * @param {string} options.token
 */
export async function resolvePoSignPortal({ token }) {
  if (!token) {
    throw new Error('token is required');
  }
  const fn = callable('resolvePoSignPortal');
  const { data } = await fn({ token: cleanString(token) });
  return data || null;
}

/**
 * @param {object} options
 * @param {string} options.token
 * @param {string} [options.name]
 * @param {string|null} [options.imageDataUrl]
 * @param {boolean} [options.clear]
 */
export async function submitPoVendorSignature({
  token,
  name = '',
  imageDataUrl = null,
  clear = false,
} = {}) {
  if (!token) {
    throw new Error('token is required');
  }
  const fn = callable('submitPoVendorSignature');
  const { data } = await fn({
    token: cleanString(token),
    name: cleanString(name),
    imageDataUrl: imageDataUrl || null,
    clear: Boolean(clear),
  });
  return data || null;
}
